import React, { ChangeEvent, FC, MouseEvent, useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  SelectChangeEvent,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Check } from '@mui/icons-material';
import SurveyContactData from '../../../../components/SurveyContactData/SurveyContactData';
import DesignChanger from '../../../../components/DesignChanger/DesignChanger';
import { ReactComponent as FlagDe } from '../../../../assets/images/flags/de.svg';
import { ReactComponent as FlagGb } from '../../../../assets/images/flags/gb.svg';
import { ReactComponent as FlagNl } from '../../../../assets/images/flags/nl.svg';
import { IPlatform } from '../../../../shared/interfaces/IPlatform';
import PlatformService from '../../../../services/PlatformService';
import { IDesign } from '../../../../shared/interfaces/IDesign';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../../providers/AuthProvider';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import { ITrendEntry } from '../../../../shared/interfaces/ITrendEntry';
import { IContactData } from '../../../../shared/interfaces/IContactData';
import PlatformSelect from '../../../../components/PlatformSelect/PlatformSelect';
import { ISurvey } from '../../../../shared/interfaces/ISurvey';
import SurveyService from '../../../../services/SurveyService';
import SurveyUtils from '../../../../shared/SurveyUtils';
import StringUtils from '../../../../shared/StringUtils';

const defaultDesign: IDesign = {
  companyLogo: '',
  primaryColor: '#357E8B',
  secondaryColor: '#000000',
  customFont: '',
  customFontFormat: '',
  customCss: '',
};

const defaultContactData: IContactData = {
  privacyPolicyLink: '',
  contactEmail: '',
  imprintLink: '',
  logoLink: '',
};

const defaultTrendData: ITrendEntry = {
  archived: false,
  archivedAt: '',
  averageRating: 0,
  companyName: '',
  companyWebsiteUrl: '',
  contactForm: false,
  createdAt: '',
  customFontFormat: '',
  customCss: '',
  id: '',
  label: '',
  lang: 'de',
  note: '',
  platform: '',
  question: 'Wie zufrieden sind Sie bei [Firmenname]?',
  questionEditable: false,
  score: 8,
  totalCount: 0,
  type: 'TREND',
  updatedAt: '',
  platformUrl: '',
  platformName: '',
  feedbackActive: false,
  feedbackText: 'Feedback / konstruktive Kritik / Verbesserungsvorschläge',
  feedbackTitle: 'Was hat Sie zu Ihrer Bewertung veranlasst? (optional)',
  introText:
    'Ihre Zufriedenheit ist uns wichtig!\nWie zufrieden Sie bei [Firmenname] sind, können nur Sie selbst beurteilen.' +
    ' Daher möchten wir Sie bitten, an einer kurzen Stimmungsumfrage teilzunehmen.\nDie Teilnahme wird nur wenige Sekunden in Anspruch nehmen und ist selbstverständlich anonym. Es werden keine personenbezogenen Daten erhoben.',
  outroText: 'Vielen Dank für Ihre Teilnahme!\nIhr Team der [Firmenname]',
  outroTextNegative: 'Vielen Dank für Ihre Teilnahme!\nIhr Team der [Firmenname]',
  negativeText: 'Ich bin unzufrieden.',
  neutralText: 'Ich fühle mich ganz ok.',
  positiveText: 'Ich bin zufrieden.',
  title: 'Bitte teilen Sie uns Ihre aktuelle Stimmung mit!',
  slug: '',
  i18n: {},
  shouldShowPrivacyNotice: false,
  privacyNoticeLink: '',
  privacyNoticeText: 'Ich stimme zu, dass meine Daten zu Analysezwecken gespeichert werden.',
};

interface Props {
  id?: string;
  loadSurveyType?: string;
  isEdit?: boolean;
  reload: () => Promise<void>;
  hasAnswers: boolean;
}

const MoodBarometerGuide: FC<Props> = ({ id, isEdit, reload, loadSurveyType, hasAnswers }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasRole } = useContext(AuthContext);

  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentSurveyGuideStep, setCurrentSurveyGuideStep] = useState(0);
  const [canEditDesign, setCanEditDesign] = useState(false);
  const [design, setDesign] = useState<IDesign>(defaultDesign);
  const [showContactData, setShowContactData] = useState(defaultTrendData.contactForm);
  const [trendData, setTrendData] = useState<ITrendEntry>(defaultTrendData);
  const [showUrlError, setShowUrlError] = useState<boolean>(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(trendData.feedbackActive);
  const [contactData, setContactData] = useState<IContactData>(defaultContactData);
  const [surveyID, setSurveyID] = useState<string>(id || '');

  const handleNextSurveyGuideStep = () => {
    setCurrentSurveyGuideStep((prevSurveyGuideStep) => prevSurveyGuideStep + 1);
  };

  const handlePreviousSurveyGuideStep = () => {
    setCurrentSurveyGuideStep((prevSurveyGuideStep) => prevSurveyGuideStep - 1);
  };

  const getMergedState = (designOverride: IDesign = design): ISurvey => ({
    ...trendData,
    ...contactData,
    ...designOverride,
  });
  const handleAddSurvey = async () => {
    const mergedState: ISurvey = getMergedState();
    if (isEdit) {
      if (!id) return;
      await SurveyService.updateTrendSurvey(id, mergedState);
    } else {
      mergedState.slug = '';
      await SurveyService.addTrendSurvey(mergedState);
    }
    await reload();
  };

  const handleOutroTextChange = (companyName: string) => {
    const value = trendData.outroText.replace(/\[Firmenname\]/g, companyName || '[Firmenname]');
    const valueNegative = trendData.outroTextNegative.replace(
      /\[Firmenname\]/g,
      companyName || '[Firmenname]'
    );
    setTrendData((prevState) => ({
      ...prevState,
      outroText: value,
      outroTextNegative: valueNegative,
    }));
  };

  const handleQuestionChange = (companyName: string) => {
    const value = trendData.question.replace(/\[Firmenname\]/g, companyName || '[Firmenname]');
    setTrendData((prevState) => ({
      ...prevState,
      question: value,
    }));
  };

  const handleIntroTextChange = (companyName: string) => {
    const value = trendData.introText.replace(/\[Firmenname\]/g, companyName || '[Firmenname]');
    setTrendData((prevState) => ({
      ...prevState,
      introText: value,
    }));
  };

  useEffect(() => {
    const controller = new AbortController();

    const getPlatforms = async (signal: AbortSignal) => {
      const platforms = await PlatformService.getPlatforms(setLoading, signal);
      if (!platforms) return;

      setPlatforms([...platforms, PlatformService.getOthersPseudoPlatform(t)]);
    };

    (async () => await getPlatforms(controller.signal))();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!hasRole('can-use-survey')) {
      navigate('/');
    }
  }, [hasRole, navigate]);

  useEffect(() => {
    setTrendData((prevState) => ({
      ...prevState,
      feedbackActive: isCheckboxChecked,
    }));
  }, [isCheckboxChecked]);

  useEffect(() => {
    if (!surveyID) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const getSurvey = async () => {
      setLoading(true);
      try {
        const endpointPath = SurveyUtils.getEndpointByType(loadSurveyType);
        const survey = await SurveyService.getSurveyPublic(
          surveyID,
          endpointPath,
          setLoading,
          signal
        );
        if (!survey) {
          setLoading(false);
          return;
        }
        if (survey.type === 'BAROMETER') {
          setTrendData(survey);
        }
        setDesign({
          companyLogo: survey.companyLogo,
          primaryColor: survey.primaryColor,
          secondaryColor: survey.secondaryColor,
          customFont: survey.customFont,
          customFontFormat: survey.customFontFormat,
          customCss: survey.customCss ?? '',
        });
        setContactData({
          privacyPolicyLink: survey.privacyPolicyLink,
          contactEmail: survey.contactEmail,
          imprintLink: survey.imprintLink,
          logoLink: survey.logoLink,
        });
        setIsCheckboxChecked(survey.feedbackActive);
        setShowContactData(
          survey.imprintLink !== '' || survey.contactEmail !== '' || survey.privacyPolicyLink !== ''
        );
        setCanEditDesign(
          survey.primaryColor !== '#357E8B' ||
            survey.secondaryColor !== '#000000' ||
            survey.companyLogo !== '' ||
            !!survey.customCss
        );
      } catch (error) {
        console.error('Failed to fetch survey:', error);
      } finally {
        setLoading(false);
      }
    };

    getSurvey();

    return () => {
      controller.abort();
    };
  }, [surveyID]);

  const shouldRender = (stepId: string) => {
    const ALWAYS_EDITABLE = ['survey_label', 'title_and_intro', 'comment', 'outro_page'];
    const RENDER_WHEN_FRESH: string[] = ['question', 'mood_check', 'portal_data'];

    if (!isEdit) {
      return true;
    }

    if (ALWAYS_EDITABLE.includes(stepId)) {
      return true;
    }

    if (!hasAnswers && RENDER_WHEN_FRESH.includes(stepId)) {
      return true;
    }

    return false;
  };

  return (
    <>
      <Box sx={{ maxWidth: '600px' }}>
        <Stepper
          activeStep={currentSurveyGuideStep}
          orientation="vertical"
        >
          {shouldRender('survey_label') && (
            <Step>
              <StepLabel>{t('survey_label')}</StepLabel>
              <StepContent>
                <TextField
                  label={t('survey_name')}
                  placeholder={'z.B.: Musterfirma'}
                  size="small"
                  variant="outlined"
                  value={trendData.label}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setTrendData((prevState) => ({
                      ...prevState,
                      label: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: '400px' }}
                  autoFocus
                />
                <Box>
                  <Button
                    disabled={trendData.label === ''}
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('company_data_label') && (
            <Step>
              <StepLabel>{t('company_data_label')}</StepLabel>
              <StepContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 284,
                  }}
                >
                  <TextField
                    label={t('company_name')}
                    size={'small'}
                    variant="outlined"
                    value={trendData.companyName}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setTrendData((prevState) => ({
                        ...prevState,
                        companyName: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                  />
                  <TextField
                    label={t('enter_company_url')}
                    size="small"
                    variant="outlined"
                    placeholder={'z.B.: example.com'}
                    multiline
                    maxRows={'2'}
                    value={trendData.companyWebsiteUrl}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setTrendData((prevState) => ({
                        ...prevState,
                        companyWebsiteUrl: event.target.value,
                      }));
                      setShowUrlError(false);
                    }}
                    error={showUrlError}
                    helperText={showUrlError ? t('url_invalid') : ''}
                    sx={{
                      minWidth: '284px',
                      mt: theme.spacing(2),
                      color: showUrlError ? 'red' : 'inherit',
                    }}
                    fullWidth
                  />
                </Box>
                <Box>
                  <Button
                    disabled={
                      !trendData.companyName || !trendData.companyWebsiteUrl || showUrlError
                    }
                    variant="contained"
                    onClick={() => {
                      if (!StringUtils.isValidCompanyUrl(trendData.companyWebsiteUrl)) {
                        setShowUrlError(true);
                        return;
                      }
                      handleNextSurveyGuideStep();
                      handleOutroTextChange(trendData.companyName);
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('title_and_intro') && (
            <Step>
              <StepLabel>{t('title_and_intro')}</StepLabel>
              <StepContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 284,
                  }}
                >
                  <TextField
                    label={t('title')}
                    size={'small'}
                    multiline
                    maxRows={'3'}
                    variant={'outlined'}
                    value={trendData.title}
                    placeholder={t('mood_barometer_placeholder')}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setTrendData((prevState) => ({
                        ...prevState,
                        title: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                  />
                  <TextField
                    label={'Intro'}
                    size={'small'}
                    multiline
                    maxRows={'6'}
                    variant={'outlined'}
                    value={trendData.introText}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setTrendData((prevState) => ({
                        ...prevState,
                        introText: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                  />
                </Box>
                <Box>
                  <Button
                    //disabled={NpsData.question === ''}
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                      handleQuestionChange(trendData.companyName);
                      handleIntroTextChange(trendData.companyName);
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('question') && (
            <Step>
              <StepLabel>{t('question')}</StepLabel>
              <StepContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 284,
                  }}
                >
                  <TextField
                    label={t('question')}
                    size={'small'}
                    multiline
                    maxRows={'3'}
                    variant={'outlined'}
                    value={trendData.question}
                    placeholder={t('how_satisfied_placeholder')}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setTrendData((prevState) => ({
                        ...prevState,
                        question: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                  />
                </Box>
                <Box>
                  <Button
                    //disabled={NpsData.question === ''}
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('comment') && (
            <Step>
              <StepLabel>{t('sentiment_survey')}</StepLabel>
              <StepContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                  <TextField
                    label={t('positive')}
                    size={'small'}
                    multiline
                    maxRows={'2'}
                    disabled={!shouldRender('mood_check')}
                    variant={'outlined'}
                    value={trendData.positiveText}
                    placeholder={t('am_satisfied')}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setTrendData((prevState) => ({
                        ...prevState,
                        positiveText: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SentimentSatisfiedAltIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label={t('neutral')}
                    size={'small'}
                    multiline
                    maxRows={'2'}
                    variant={'outlined'}
                    disabled={!shouldRender('mood_check')}
                    value={trendData.neutralText}
                    placeholder={t('am_neutral')}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setTrendData((prevState) => ({
                        ...prevState,
                        neutralText: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SentimentSatisfiedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label={t('negative')}
                    size={'small'}
                    multiline
                    maxRows={'2'}
                    variant={'outlined'}
                    disabled={!shouldRender('mood_check')}
                    value={trendData.negativeText}
                    placeholder={t('am_dissatisfied')}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setTrendData((prevState) => ({
                        ...prevState,
                        negativeText: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SentimentVeryDissatisfiedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Typography>
                  {t('enable_comment_field')}
                  <Checkbox
                    checked={isCheckboxChecked}
                    onChange={() => setIsCheckboxChecked(!isCheckboxChecked)}
                  />
                </Typography>
                <Box>
                  <Button
                    // disabled={trendData.question === ''}
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {isCheckboxChecked && shouldRender('comment') && (
            <Step>
              <StepLabel>{`${t('comment')} (${t('optional_configuration')})`} </StepLabel>
              <StepContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                  <TextField
                    label={t('comment_field_title')}
                    size={'small'}
                    multiline
                    maxRows={'2'}
                    variant={'outlined'}
                    value={trendData.feedbackTitle}
                    placeholder={'Was hat Sie zu Ihrer Bewertung veranlasst? (optional)'}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setTrendData((prevState) => ({
                        ...prevState,
                        feedbackTitle: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                  />
                  <TextField
                    label={'Kommentarfeld'}
                    size={'medium'}
                    multiline
                    maxRows={'3'}
                    variant={'outlined'}
                    value={trendData.feedbackText}
                    placeholder={
                      'Inhalt im Kommentarfeld: Feedback, konstruktive Kritik, Verbesserungsvorschläge'
                    }
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setTrendData((prevState) => ({
                        ...prevState,
                        feedbackText: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                  />
                  <Typography>
                    {t('show_privacy_notice')}{' '}
                    <Checkbox
                      checked={trendData.shouldShowPrivacyNotice}
                      onChange={() => {
                        setTrendData((prevState) => ({
                          ...prevState,
                          shouldShowPrivacyNotice: !prevState.shouldShowPrivacyNotice,
                          privacyNoticeText: prevState.privacyNoticeText
                            ? prevState.privacyNoticeText
                            : defaultTrendData.privacyNoticeText,
                        }));
                      }}
                    />
                  </Typography>

                  {trendData.shouldShowPrivacyNotice && (
                    <>
                      <TextField
                        label={t('survey_privacy_notice')}
                        size={'medium'}
                        multiline
                        maxRows={'3'}
                        variant={'outlined'}
                        value={trendData.privacyNoticeText}
                        placeholder={t('survey_privacy_notice_placeholder')}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setTrendData((prevState) => ({
                            ...prevState,
                            privacyNoticeText: event.target.value,
                          }));
                        }}
                        sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                      />
                      <TextField
                        label={t('survey_privacy_notice_link')}
                        size={'medium'}
                        maxRows={'3'}
                        variant={'outlined'}
                        required={true}
                        value={trendData.privacyNoticeLink}
                        placeholder={t('survey_privacy_notice_link_placeholder')}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setTrendData((prevState) => ({
                            ...prevState,
                            privacyNoticeLink: event.target.value,
                          }));
                        }}
                        sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                      />
                    </>
                  )}
                </Box>
                <Box>
                  <Button
                    disabled={
                      trendData.shouldShowPrivacyNotice &&
                      (!trendData.privacyNoticeLink || trendData.privacyNoticeLink === '')
                    }
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    // disabled
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('portal_data') && (
            <Step>
              <StepLabel>{`${t('portal_data')} (${t('optional_configuration')})`}</StepLabel>
              <StepContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                  <PlatformSelect
                    value={trendData.platform}
                    onChange={(event: SelectChangeEvent) => {
                      setTrendData((prevState) => ({
                        ...prevState,
                        platform: event.target.value,
                      }));
                    }}
                    platforms={platforms}
                  />
                  <TextField
                    label={`${t('portal_data')}-URL`}
                    size="small"
                    variant="outlined"
                    placeholder={'z.B.: https://www.kununu.de/bewertungen'}
                    value={trendData.platformUrl}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setTrendData((prevState) => ({
                        ...prevState,
                        platformUrl: event.target.value,
                      }));
                      setShowUrlError(false);
                    }}
                    error={showUrlError}
                    helperText={showUrlError ? t('url_invalid') : ''}
                    sx={{ mt: theme.spacing(2), color: showUrlError ? 'red' : 'inherit' }}
                    fullWidth
                  />
                </Box>
                <Box>
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (
                        !StringUtils.isValidPlatformUrl(trendData.platformUrl) &&
                        trendData.platformUrl !== ''
                      ) {
                        setShowUrlError(true);
                        return;
                      }
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    disabled={false}
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('outro_page') && (
            <Step>
              <StepLabel>{t('final_page')}</StepLabel>
              <StepContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                  <TextField
                    label={t('outro_text')}
                    size={'small'}
                    multiline
                    maxRows={'2'}
                    variant={'outlined'}
                    value={trendData.outroText}
                    placeholder={'Vielen Dank für Ihre Teilnahme!\nIhr Team der Musterfirma GmbH'}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setTrendData((prevState) => ({
                        ...prevState,
                        outroText: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                  <TextField
                    label={t('outro_text_if_negative')}
                    size={'small'}
                    multiline
                    maxRows={'2'}
                    variant={'outlined'}
                    value={trendData.outroTextNegative}
                    placeholder={t('outro_text_if_negative_placeholder')}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setTrendData((prevState) => ({
                        ...prevState,
                        outroTextNegative: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                  />
                </Box>
                <Box>
                  <Button
                    //disabled={NpsData.question === ''}
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                      handleOutroTextChange(trendData.companyName);
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          <Step>
            <StepLabel>{t('finish_configuration')}</StepLabel>
            <StepContent>
              <Box>
                <Button
                  variant="contained"
                  startIcon={<Check />}
                  sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  onClick={handleAddSurvey}
                >
                  {t('finish')}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handlePreviousSurveyGuideStep}
                  sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                >
                  {t('back')}
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </Box>

      {!isEdit ? (
        <>
          <Box sx={{ display: 'flex', minHeight: '300px', mx: theme.spacing(6) }}>
            <Divider
              orientation="vertical"
              flexItem
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'flex-start',
              minWidth: '400px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: theme.spacing(2) }}
            >
              {t('optional_configuration')}
            </Typography>
            <FormGroup>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: theme.spacing(1),
                  }}
                >
                  <Typography>{t('language')}</Typography>
                  <ToggleButtonGroup
                    color="primary"
                    size="small"
                    value={trendData.lang}
                    exclusive
                    onChange={(event: MouseEvent, value: string | null) => {
                      if (value) {
                        setTrendData((prevState) => ({
                          ...prevState,
                          lang: value,
                        }));
                      }
                    }}
                    sx={{
                      '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                        border: `1px solid ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    <ToggleButton value="de">
                      <FlagDe
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                    <ToggleButton value="en">
                      <FlagGb
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canEditDesign}
                      onChange={() => {
                        setCanEditDesign((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">Design</Typography>}
                />

                {canEditDesign && (
                  <DesignChanger
                    defaultDesign={design}
                    onChange={setDesign}
                    surveyType="BAROMETER"
                    buildPreviewSurvey={getMergedState}
                    savedSurveyId={surveyID}
                  />
                )}
                <FormControlLabel
                  sx={{ mt: theme.spacing(2) }}
                  control={
                    <Switch
                      checked={showContactData}
                      onChange={() => {
                        setShowContactData((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">{t('contact_data')}</Typography>}
                />
              </Box>
            </FormGroup>
            {showContactData && (
              <SurveyContactData
                privacyPolicyLink={contactData.privacyPolicyLink}
                contactEmail={contactData.contactEmail}
                imprintLink={contactData.imprintLink}
                contactForm={showContactData}
                logoLink={contactData.logoLink}
                onChange={setContactData}
              />
            )}
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', minHeight: '300px', mx: theme.spacing(6) }}>
            <Divider
              orientation="vertical"
              flexItem
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'flex-start',
              minWidth: '400px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: theme.spacing(2) }}
            >
              {t('optional_configuration')}
            </Typography>
            <FormGroup>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: theme.spacing(1),
                  }}
                >
                  <Typography>{t('language')}</Typography>
                  <ToggleButtonGroup
                    color="primary"
                    size="small"
                    value={trendData.lang}
                    exclusive
                    onChange={(event: MouseEvent<HTMLElement>, value: string | null) => {
                      if (value) {
                        setTrendData((prevState) => ({
                          ...prevState,
                          lang: value,
                        }));
                      }
                    }}
                    sx={{
                      '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                        border: `1px solid ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    <ToggleButton value="de">
                      <FlagDe
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                    <ToggleButton value="en">
                      <FlagGb
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canEditDesign}
                      onChange={() => {
                        setCanEditDesign((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">Design</Typography>}
                />

                {canEditDesign && (
                  <DesignChanger
                    defaultDesign={design}
                    onChange={setDesign}
                    surveyType="BAROMETER"
                    buildPreviewSurvey={getMergedState}
                    savedSurveyId={surveyID}
                  />
                )}
                <FormControlLabel
                  sx={{ mt: theme.spacing(2) }}
                  control={
                    <Switch
                      checked={showContactData}
                      onChange={() => {
                        setShowContactData((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">{t('contact_data')}</Typography>}
                />
              </Box>
            </FormGroup>
            {showContactData && (
              <SurveyContactData
                privacyPolicyLink={contactData.privacyPolicyLink}
                contactEmail={contactData.contactEmail}
                imprintLink={contactData.imprintLink}
                contactForm={showContactData}
                onChange={setContactData}
                logoLink={contactData.logoLink}
              />
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default MoodBarometerGuide;
